import React, {useEffect, useState} from 'react';

import {
  AnalysisSourcesForConfigOrExecution,
  AnalysisSourcesOrigin,
} from 'procedures/design/ProcedureMethodAnalysisTypes';
import ProcedureMethodAnalysisSourceTable, {
  SelectedSources,
} from 'procedures/shared-components/analysis/ProcedureMethodAnalysisSourceTable';
import {
  convertSingleSourceIntoSelectedSources,
  extractSingleSource,
} from 'procedures/shared-components/analysis/procedureMethodAnalysisUtils';
import {CenteredSpinner} from 'ui-library/Spinner';

interface BaseAnalysisTemplateSourceTableProps {
  sources: AnalysisSourcesForConfigOrExecution | null;
  analysisSourcesOrigin: AnalysisSourcesOrigin;
  navigateToTests?: () => void;
}

interface SelectableAnalysisTemplateSourceTableProps extends BaseAnalysisTemplateSourceTableProps {
  initialSelectedSource: string | null | undefined;
  setSelectedSource: (selectedSource: string | null) => void;
}

interface StaticAnalysisTemplateSourceTableProps extends BaseAnalysisTemplateSourceTableProps {
  initialSelectedSource?: never;
  setSelectedSource?: never;
}

type AnalysisTemplateSourceTableProps =
  | SelectableAnalysisTemplateSourceTableProps
  | StaticAnalysisTemplateSourceTableProps;

export default function AnalysisTemplateSourceTable(props: AnalysisTemplateSourceTableProps): JSX.Element {
  const {sources, setSelectedSource, navigateToTests} = props;

  const [_selectedSources, _setSelectedSources] = useState<SelectedSources>(
    convertSingleSourceIntoSelectedSources(
      props.initialSelectedSource !== undefined ? props.initialSelectedSource : null,
      sources
    )
  );

  useEffect(() => {
    setSelectedSource?.(extractSingleSource(_selectedSources));
  }, [setSelectedSource, _selectedSources]);

  const selectableProps =
    setSelectedSource == null
      ? // Yes, this looks weird, but it helps TypeScript understand intent (a static value, rather than a general boolean)
        {isSelectorHidden: true as const}
      : {
          isSelectorHidden: false as const,
          selectedSources: _selectedSources,
          setSelectedSources: _setSelectedSources,
          multiSelect: false,
        };

  return sources != null ? (
    <ProcedureMethodAnalysisSourceTable
      availableSourcesPerStep={sources.sourcesPerStep}
      availableParameterTypes={sources.availableParameterTypes}
      analysisSourcesOrigin={props.analysisSourcesOrigin}
      includeEmptyDataSources={true}
      isColumnHidden={
        props.analysisSourcesOrigin === AnalysisSourcesOrigin.CONFIGURATION
          ? // Hide creationTime column since it will always be N/A in config
            (accessorKey) => accessorKey === 'creationTime'
          : undefined
      }
      navigateToTests={navigateToTests}
      {...selectableProps}
    />
  ) : (
    <CenteredSpinner />
  );
}
